// Company News section: heading + an array of news items, each with a
// title, rich-text body, and up to 3 images. Each item has its own
// inline edit mode keyed off `${section.id}-${index}`.

import { useState } from "react"
import { Box, Button, Card, IconButton, Popover, TextField, Typography } from "@mui/material"
import TipTapEditor from "@crm-src/components/tiptap-editor"
import { InlineAiGenerator } from "@crm-src/components/inline-ai-generator"
import { AiTextImprover } from "@crm-src/components/ai-text-improver"
import { NewsImageSelector } from "@crm-src/components/news-image-selector"
import { Trash2, Plus, Palette, Type } from "lucide-react"
// DOMPurify-backed sanitizer applied to user/AI-generated rich text in
// the visual preview, matching main's protection.
import { sanitizeHtmlWithStyles } from "@crm-src/lib/sanitize"
import { NewsletterSection, toAbsoluteUrl, toPreviewImageUrl } from "./stock-images"

interface CompanyNewsSectionProps {
  section: NewsletterSection
  editingSection: string | null
  onUpdate: (sectionId: string, partial: any) => void
  onChangeColor: (sectionId: string, color: string) => void
  onDelete: (sectionId: string) => void
  setEditingSection: (id: string | null) => void
  onAddNewsItem: () => void
}

export function CompanyNewsSection({
  section,
  editingSection,
  onUpdate,
  onChangeColor,
  onDelete,
  setEditingSection,
  onAddNewsItem,
}: CompanyNewsSectionProps) {
  const [colorAnchor, setColorAnchor] = useState<null | HTMLElement>(null)
  return (
    <Box
      className="group"
      sx={{ position: "relative" }}
      style={{ backgroundColor: section.backgroundColor, padding: '17px 40px', borderTop: '3px solid #6B8E23' }}
    >
      {/* Section controls */}
      <Box sx={{ position: "absolute", top: 8, right: 8, display: "flex", gap: 1, opacity: 0, transition: "opacity 0.15s", zIndex: 10, ".group:hover &": { opacity: 1 } }}>
        <IconButton size="small" onClick={(e) => setColorAnchor(e.currentTarget)}>
          <Palette size={16} />
        </IconButton>
        <Popover
          open={Boolean(colorAnchor)}
          anchorEl={colorAnchor}
          onClose={() => setColorAnchor(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          slotProps={{ paper: { sx: { width: 288, p: 2 } } }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <Typography component="label" sx={{ fontSize: "1rem", fontWeight: 600 }}>Background Color</Typography>
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 1.5 }}>
              {[
                { color: '#f9fdf7', label: 'Light Green' },
                { color: '#ffffff', label: 'White' },
                { color: '#e8f5e9', label: 'Soft Green' },
                { color: '#fff9e6', label: 'Cream' },
              ].map(({ color, label }) => (
                <Box
                  component="button"
                  key={color}
                  onClick={() => onChangeColor(section.id, color)}
                  sx={{ position: "relative", height: 64, borderRadius: 1, border: "2px solid", transition: "all 0.15s", cursor: "pointer", "&:hover": { transform: "scale(1.05)", boxShadow: 3 } }}
                  style={{ backgroundColor: color, borderColor: section.backgroundColor === color ? '#6B8E23' : '#d1d5db' }}
                >
                  <Box component="span" sx={{ position: "absolute", bottom: 4, left: 8, fontSize: 12, fontWeight: 500, color: "text.secondary" }}>{label}</Box>
                </Box>
              ))}
            </Box>
          </Box>
        </Popover>
        <Button size="small" color="inherit" variant="outlined" onClick={onAddNewsItem} startIcon={<Plus size={16} />}>
          Add News
        </Button>
        <IconButton size="small" color="error" onClick={() => onDelete(section.id)}>
          <Trash2 size={16} />
        </IconButton>
      </Box>

      {editingSection === `${section.id}-heading` ? (
        <Card sx={{ p: 2, mb: 2, display: "flex", flexDirection: "column", gap: 1.5 }}>
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
              <Typography component="label" sx={{ fontSize: 14, fontWeight: 500 }}>Section Heading</Typography>
              <Box sx={{ display: "flex", gap: 1 }}>
                <InlineAiGenerator
                  fieldType="short"
                  context="company news section heading"
                  placeholder="e.g., latest updates, what's new"
                  currentValue={section.content.heading}
                  onGenerate={(content) => onUpdate(section.id, { heading: content })}
                />
                <AiTextImprover
                  fieldType="short"
                  context="company news section heading"
                  currentText={section.content.heading}
                  onImprove={(content) => onUpdate(section.id, { heading: content })}
                />
              </Box>
            </Box>
            <TextField
              fullWidth
              size="small"
              value={section.content.heading}
              onChange={(e) => onUpdate(section.id, { heading: e.target.value })}
              placeholder="e.g., Company News"
            />
          </Box>
          <Button size="small" color="success" variant="contained" onClick={() => setEditingSection(null)}>Save</Button>
        </Card>
      ) : (
        <Box
          className="group-heading"
          sx={{ position: "relative", pb: 1.25, mb: 2.5 }}
        >
          <IconButton
            size="small"
            onClick={() => setEditingSection(`${section.id}-heading`)}
            sx={{ position: "absolute", top: -8, right: -8, color: "text.secondary", opacity: 0, ".group-heading:hover &": { opacity: 1 } }}
          >
            <Type size={16} />
          </IconButton>
          <h2 style={{ color: '#8B4513', fontSize: '24px', margin: '0 0 10px 0', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", Arial, sans-serif', borderBottom: '2px solid #f0e68c', paddingBottom: '10px' }}>
            {section.content.heading || 'Company News'}
          </h2>
        </Box>
      )}

      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {section.content.items.map((item: any, index: number) => (
          <Box
            key={index}
            className="group-item"
            sx={{ position: "relative" }}
            style={{ marginBottom: index === section.content.items.length - 1 ? '0' : '30px', paddingBottom: index === section.content.items.length - 1 ? '0' : '30px', borderBottom: index < section.content.items.length - 1 ? '2px solid #e2e8f0' : 'none' }}
            data-news-item-index={`${section.id}-${index}`}
          >
            <Box sx={{ position: "absolute", top: 0, right: 0, display: "flex", gap: 1, bgcolor: "rgba(255,255,255,0.9)", backdropFilter: "blur(4px)", borderRadius: 1, boxShadow: 1, p: 0.5 }}>
              <IconButton
                size="small"
                onClick={() => setEditingSection(`${section.id}-${index}`)}
              >
                <Type size={16} />
              </IconButton>
              <IconButton
                size="small"
                color="error"
                onClick={() => {
                  const newItems = section.content.items.filter((_: any, i: number) => i !== index)
                  onUpdate(section.id, { items: newItems })
                }}
              >
                <Trash2 size={16} />
              </IconButton>
            </Box>

            {editingSection === `${section.id}-${index}` ? (
              <Card sx={{ p: 2, display: "flex", flexDirection: "column", gap: 1.5 }}>
                <Typography component="h4" sx={{ fontWeight: 600, mb: 1.5 }}>Edit News Item</Typography>
                <Box>
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                    <Typography component="label" sx={{ fontSize: 14, fontWeight: 500 }}>Title</Typography>
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <InlineAiGenerator
                        fieldType="short"
                        context="company news title - an engaging headline for company announcement"
                        placeholder="e.g., new product feature for oil & gas industry"
                        currentValue={item.title}
                        onGenerate={(content) => {
                          const newItems = section.content.items.map((newsItem: any, i: number) =>
                            i === index ? { ...newsItem, title: content } : { ...newsItem }
                          )
                          onUpdate(section.id, { items: newItems })
                        }}
                      />
                      <AiTextImprover
                        fieldType="short"
                        context="company news title"
                        currentText={item.title}
                        onImprove={(content) => {
                          const newItems = section.content.items.map((newsItem: any, i: number) =>
                            i === index ? { ...newsItem, title: content } : { ...newsItem }
                          )
                          onUpdate(section.id, { items: newItems })
                        }}
                      />
                    </Box>
                  </Box>
                  <TextField
                    fullWidth
                    size="small"
                    value={item.title}
                    onChange={(e) => {
                      const newItems = section.content.items.map((it: any, i: number) =>
                        i === index ? { ...it, title: e.target.value } : { ...it }
                      )
                      onUpdate(section.id, { items: newItems })
                    }}
                  />
                </Box>
                <Box>
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                    <Typography component="label" sx={{ fontSize: 14, fontWeight: 500 }}>Content (Rich Text Editor)</Typography>
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <InlineAiGenerator
                        fieldType="long"
                        context="company news content - detailed announcement text about company updates, features, or events"
                        placeholder="e.g., describe the new feature, update, or event in detail"
                        currentValue={item.text}
                        onGenerate={(content) => {
                          const newItems = section.content.items.map((newsItem: any, i: number) =>
                            i === index ? { ...newsItem, text: content } : { ...newsItem }
                          )
                          onUpdate(section.id, { items: newItems })
                        }}
                      />
                      <AiTextImprover
                        fieldType="long"
                        context="company news content"
                        currentText={item.text?.replace(/<[^>]*>/g, '') || ''}
                        onImprove={(content) => {
                          const newItems = section.content.items.map((newsItem: any, i: number) =>
                            i === index ? { ...newsItem, text: `<p>${content}</p>` } : { ...newsItem }
                          )
                          onUpdate(section.id, { items: newItems })
                        }}
                      />
                    </Box>
                  </Box>
                  <TipTapEditor
                    value={item.text}
                    onChange={(text) => {
                      const newItems = section.content.items.map((it: any, i: number) =>
                        i === index ? { ...it, text } : { ...it }
                      )
                      onUpdate(section.id, { items: newItems })
                    }}
                    placeholder="Enter your news content here... Use the toolbar to format text, add images, links, etc."
                    minHeight={200}
                  />
                </Box>
                <Box>
                  <NewsImageSelector
                    selectedImages={item.images || []}
                    onChange={(images) => {
                      const newItems = section.content.items.map((it: any, i: number) =>
                        i === index ? { ...it, images } : { ...it }
                      )
                      onUpdate(section.id, { items: newItems })
                    }}
                  />
                </Box>
                <Button size="small" color="success" variant="contained" onClick={() => setEditingSection(null)}>Save</Button>
              </Card>
            ) : (
              <div style={{ display: 'flex', gap: '15px', alignItems: 'flex-start' }}>
                {/* Images Column */}
                {item.images && item.images.length > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '80px', flexShrink: 0 }}>
                    {item.images.slice(0, 3).map((imgUrl: string, imgIndex: number) => (
                      <img
                        key={imgIndex}
                        src={`${toPreviewImageUrl(imgUrl)}?w=200&h=200&fit=crop&auto=format`}
                        alt="News image"
                        style={{
                          width: '80px',
                          height: '80px',
                          objectFit: 'cover',
                          borderRadius: '4px',
                          display: 'block'
                        }}
                      />
                    ))}
                  </div>
                )}
                {/* Content Column */}
                <div style={{ flex: 1 }}>
                  <h3 style={{ color: '#2d3748', fontSize: '20px', margin: '0 0 12px 0', fontWeight: 'bold' }}>
                    {item.title}
                  </h3>
                  <div
                    style={{ color: '#4a5568', fontSize: '15px', lineHeight: '1.7', margin: 0 }}
                    dangerouslySetInnerHTML={{ __html: sanitizeHtmlWithStyles(item.text) }}
                  />
                </div>
              </div>
            )}
          </Box>
        ))}
      </Box>
    </Box>
  )
}

export function companyNewsToHtml(section: NewsletterSection): string {
  return `
          <!-- Company News Section -->
          <tr>
            <td style="background-color: ${section.backgroundColor || '#fff9e6'}; padding: 17px 40px;">
              <h2 style="color: #8B4513; font-size: 24px; margin: 0 0 20px 0; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Helvetica Neue', Arial, sans-serif; border-bottom: 2px solid #f0e68c; padding-bottom: 10px;">
                ${section.content.heading || 'Company News'}
              </h2>
              ${section.content.items.map((item: any, index: number) => {
                const hasImages = item.images && item.images.length > 0
                return `
              <table width="100%" cellpadding="0" cellspacing="0" border="0" role="presentation" style="margin-bottom: ${index === section.content.items.length - 1 ? '0' : '25px'};">
                <tr>
                  ${hasImages ? `
                  <!-- Images Column — fixed 80px. Outlook's Word engine IGNORES
                       CSS width/height on <img> and does not support object-fit,
                       so the HTML width/height ATTRIBUTES below are what actually
                       size the thumbnail. Without them Outlook paints the
                       full-resolution photo at its intrinsic size, blowing the
                       image column out to full width and squeezing the text into
                       a one-word-per-line sliver. Explicit td width + valign
                       attributes keep Word's table auto-layout honest too. -->
                  <td width="80" valign="top" style="width: 80px; vertical-align: top;">
                    ${item.images.slice(0, 3).map((imgUrl: string) => `
                    <img src="${toAbsoluteUrl(imgUrl)}" alt="News image" width="80" height="80" style="width: 80px; height: 80px; object-fit: cover; border-radius: 4px; margin-bottom: 8px; display: block; border: 0;" />
                    `).join('')}
                  </td>
                  ` : ''}
                  <!-- Content Column — the 15px gap lives here as padding-left so
                       it collapses cleanly when there is no image column. -->
                  <td valign="top" style="vertical-align: top;${hasImages ? ' padding-left: 15px;' : ''}">
                    <h3 style="color: #2d3748; font-size: 18px; margin: 0 0 10px 0; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Helvetica Neue', Arial, sans-serif; font-weight: bold;">
                      ${item.title}
                    </h3>
                    <div style="color: #2d3748; font-size: 14px; line-height: 1.6;">
                      ${item.text}
                    </div>
                  </td>
                </tr>
              </table>
              `}).join('')}
            </td>
          </tr>
`
}
